import React, { useState, useEffect } from 'react';
import {
    makeStyles,
    createStyles,
    Theme,
} from '@material-ui/core/styles';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import SmsIcon from '@material-ui/icons/Sms';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import { EmailTemplateDialog, EmailTemplateDialogProps } from './dialogs/EmailTemplateDialog';
import { DeleteConfirmationDialog } from '../components/Dialogs/DeleteConfirmationDialog';
import { styleMergeFields } from '../helpers/mergeFieldHelper';
import {
    EmailTemplateList,
    EmailTemplateListQuery,
    EmailTemplateListQueryData,
    EmailTemplateListQueryParams,
    EmailTemplateNotificationData,
    EmailTemplateTypeEnum,
    MessageTypeEnum,
    subscriptionEmailTemplate,
} from './EmailTemplateRespository';
import { useQuery, useSubscription } from 'react-apollo';
import { retrieveQueryDataAsync } from '../helpers/commonHelper';
import {
    FilteredVerticalTabPage,
    SidebarGroup,
    SidebarItem,
} from '../v2/layouts/FilteredVerticalTabPage';
import gql from 'graphql-tag';
import { SkeletonLoader } from '../components/loading/SkeletonLoader';
import { DocumentFile } from '../types/DocumentFile';
import { client } from '..';
import { showNotification } from '../App';

// ─── Sidebar group ────────────────────────────────────────────────────────────

const TEMPLATE_GROUPS: SidebarGroup[] = [
    {
        key: 'mail',
        label: 'Email',
        icon: <MailOutlineIcon fontSize="small" />,
        iconColor: '#1565c0',
    },
    {
        key: 'sms',
        label: 'SMS',
        icon: <SmsIcon fontSize="small" />,
        iconColor: '#1565c0',
    }
];

// ─── Styles (preview pane only) ───────────────────────────────────────────────

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        previewHeader: {
            backgroundColor: '#fff',
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1, 1.5),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(2),
            flexShrink: 0,
        },
        previewMeta: {
            flex: 1,
            minWidth: 0,
        },
        previewSubject: {
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        previewDesc: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            marginTop: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        previewActions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            flexShrink: 0,
        },
        actionBtn: {
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 6,
            boxShadow: 'none',
            minWidth: 0,
            paddingLeft: theme.spacing(1.5),
            paddingRight: theme.spacing(1.5),
            '&:hover': { boxShadow: 'none' },
        },
        deleteBtn: {
            color: theme.palette.text.secondary,
            '&:hover': {
                color: theme.palette.error.main,
                backgroundColor: '#ffebee',
            },
        },
        emailCard: {
            backgroundColor: '#fff',
            borderRadius: 10,
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        },
        emailBody: {
            padding: theme.spacing(3),
            fontSize: 13,
            lineHeight: 1.7,
            color: theme.palette.text.primary,
            fontFamily: 'Helvetica, Arial, sans-serif',
            '& p': { margin: '0 0 12px' },
            '& p:last-child': { marginBottom: 0 },
            '& .mergeFieldToken': {
                color: '#1565c0',
                fontFamily: 'Consolas, Monaco, monospace',
                backgroundColor: 'rgba(21, 101, 192, 0.08)',
                padding: '1px 4px',
                borderRadius: 3,
                fontSize: '0.9em',
            },
        },
        attachmentsWrap: {
            borderTop: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(2.25, 3, 3),
            background: 'linear-gradient(180deg, #fcfdff 0%, #f8faff 100%)',
        },
        attachmentsTitle: {
            fontSize: 11,
            fontWeight: 800,
            marginBottom: theme.spacing(1.25),
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: 0.8,
        },
        attachmentsList: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1),
        },
        attachmentLink: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(1.25),
            width: '100%',
            backgroundColor: '#ffffff',
            borderRadius: 10,
            padding: theme.spacing(1.1, 1.25),
            textDecoration: 'none',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 2px rgba(13, 27, 62, 0.04)',
            transition: 'all 120ms ease',
            '&:hover': {
                borderColor: theme.palette.primary.light,
                boxShadow: '0 4px 12px rgba(21, 101, 192, 0.16)',
                transform: 'translateY(-1px)',
            },
        },
        attachmentLinkDisabled: {
            opacity: 0.6,
            cursor: 'not-allowed',
        },
        attachmentLeft: {
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            gap: theme.spacing(1),
        },
        attachmentIconWrap: {
            width: 30,
            height: 30,
            borderRadius: 8,
            backgroundColor: '#e3f2fd',
            color: '#1565c0',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': {
                fontSize: 16,
            },
        },
        attachmentMeta: {
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
        },
        attachmentName: {
            fontSize: 12,
            fontWeight: 600,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        attachmentSub: {
            fontSize: 11,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        attachmentRight: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: theme.spacing(0.75),
            flexShrink: 0,
            color: '#1565c0',
        },
        attachmentExt: {
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.6,
            color: '#1565c0',
            backgroundColor: '#e3f2fd',
            borderRadius: 999,
            padding: theme.spacing(0.2, 0.7),
        },
        attachmentDownload: {
            display: 'inline-flex',
            alignItems: 'center',
            '& svg': {
                fontSize: 16,
            },
        },
        noSelection: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(6, 2),
            gap: theme.spacing(1.5),
            color: theme.palette.text.disabled,
        },
        noSelectionIcon: {
            fontSize: 48,
            color: theme.palette.grey[300],
        },
        noSelectionTitle: {
            fontSize: 14,
            fontWeight: 600,
            color: theme.palette.text.secondary,
        },
        noSelectionSub: {
            fontSize: 12,
            color: theme.palette.text.disabled,
            textAlign: 'center',
        },
        noSelectionBtn: {
            marginTop: theme.spacing(0.5),
            fontSize: 13,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 8,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
        },
    })
);

// ─── Component ────────────────────────────────────────────────────────────────

export interface EmailTemplateContentWithSidebarProps extends Pick<EmailTemplateDialogProps, 'showFields' | 'messageType' | 'areaOfLawMultiple'> {
    emailType: EmailTemplateTypeEnum;
}

export const EmailTemplateContentWithSidebar: React.FC<EmailTemplateContentWithSidebarProps> = (props) => {

    const classes = useStyles();

    const [dialogOpen,   setDialogOpen]   = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<EmailTemplateList | undefined>();
    const [selectedTemplateData, setSelectedTemplateData] = useState<EmailTemplate | null>(null);
    const [selectedSidebarItemId, setSelectedSidebarItemId] = useState<string | null>(null);
    const [defaultTemplateData, setDefaultTemplateData] = useState<EmailTemplate | null>(null);
    const [selectedTemplateLoading, setSelectedTemplateLoading] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);

    // ── Query ──────────────────────────────────────────────────────────────────

    const emailTemplateListQuery = useQuery<EmailTemplateListQueryData, EmailTemplateListQueryParams>(EmailTemplateListQuery, {
        variables: {
            emailType: EmailTemplateTypeEnum[props.emailType],
            integrationTemplateId: null,
        },
        fetchPolicy: 'cache-first',
    });

    // ── subscription ────────────────────────────────────────────────────────────────

    const emailTemplateSubscription = useSubscription<EmailTemplateNotificationData>(subscriptionEmailTemplate)

    useEffect(() => {
        const notification = emailTemplateSubscription.data?.emailTemplateNotification;

        if (emailTemplateSubscription.error) {
            showNotification('Failed', emailTemplateSubscription.error.message, 'error');
        }

        if (!notification) {
            return;
        }

        if (notification.message.toLowerCase().includes('saved successfully')) {
            showNotification('Success', notification.message, 'info');
        }

        const selectedTemplateId = selectedTemplateData?.emailTemplateGuid ?? null;

        const syncTemplatesAfterSubscription = async (): Promise<void> => {
            const refetchResult = await emailTemplateListQuery.refetch({
                emailType: EmailTemplateTypeEnum[props.emailType],
                integrationTemplateId: null,
            });

            const refreshedList = refetchResult.data?.templates?.emailTemplateList ?? [];

            if (notification.message.toLowerCase().includes('reset successfully')) {
                const fallbackTemplate = refreshedList[0];

                if (!fallbackTemplate) {
                    setSelectedTemplateData(null);
                    setSelectedSidebarItemId(null);
                    return;
                }

                setSelectedSidebarItemId(fallbackTemplate.emailTemplateGuid);
                await fetchSelectedTemplate(fallbackTemplate);
                return;
            }

            if (!refreshedList.length) {
                setSelectedTemplateData(null);
                setSelectedSidebarItemId(null);
                return;
            }

            const stillExists = selectedTemplateId
                ? refreshedList.find((item) => item.emailTemplateGuid === selectedTemplateId)
                : undefined;

            const nextTemplate = stillExists ?? refreshedList[0];
            setSelectedSidebarItemId(nextTemplate.emailTemplateGuid);
            await fetchSelectedTemplate(nextTemplate);
        };

        void syncTemplatesAfterSubscription();

    }, [emailTemplateSubscription.data, emailTemplateSubscription.loading, emailTemplateSubscription.error])


    // ── Derived ────────────────────────────────────────────────────────────────

    const list = emailTemplateListQuery.data?.templates?.emailTemplateList ?? [];
    const firstTemplate = list[0] ?? null;
    const firstTemplateId = firstTemplate?.emailTemplateGuid ?? null;

    const sidebarItems: SidebarItem[] = list.map(t => ({
        id:          t.emailTemplateGuid,
        name:        t.name || t.subject || 'Untitled',
        description: t.description,
        group:       t.messageType !== MessageTypeEnum.SMS ? 'mail' : 'sms',
        icon:        t.messageType === MessageTypeEnum.EMAIL ? <MailOutlineIcon fontSize="small" /> : <SmsIcon fontSize="small" />,
        iconBg:      '#e3f2fd',
        iconColor:   '#1565c0',
        tags: props.showFields?.areaOfLaw ? t.matchedPropertys?.matchedProperty && t.matchedPropertys?.matchedProperty.length ? t.matchedPropertys.matchedProperty.map(mp => mp.keyDescription) : ['All'] : undefined,
        isActive: t.isActive,
    }));

    const fetchSelectedTemplate = async (
        selectedTemplate: EmailTemplateList | undefined,
        forceRefresh: boolean = false,
    ): Promise<EmailTemplate | null> => {
        if (!selectedTemplate) {
            setSelectedTemplateData(null);
            return null;
        }

        setSelectedTemplateLoading(true);

        try {
            const fetchedTemplateData = await retrieveQueryDataAsync<EmailTemplateQueryParams, EmailTemplateData>({
            params: {
                areaOfLaw: null,
                emailStyle: null,
                emailTemplateGuid: selectedTemplate?.emailTemplateGuid ?? null,
                emailType: EmailTemplateTypeEnum[props.emailType],
                integrationTemplateId: selectedTemplate?.integrationTemplateId ?? null,
                isInternalRole: selectedTemplate?.isInternalRole ?? null,
                roleId: selectedTemplate?.roleGuid ?? null,
                selectedUserId: selectedTemplate?.userGuid ?? null,
            },
            query: EmailTemplateQuery,
            refreshData: forceRefresh,
        });

            const fetchedTemplate = fetchedTemplateData?.templates?.emailTemplate ?? null;
            
            setSelectedTemplateData(fetchedTemplate);

            return fetchedTemplate;
        } finally {
            setSelectedTemplateLoading(false);
        }
    };

    const fetchDefaultTemplate = async (): Promise<EmailTemplate | null> => {
        const fetchedTemplateData = await retrieveQueryDataAsync<EmailTemplateQueryParams, EmailTemplateData>({
            params: {
                areaOfLaw: null,
                emailStyle: null,
                emailTemplateGuid: null,
                emailType: EmailTemplateTypeEnum[props.emailType],
                integrationTemplateId: null,
                isInternalRole: null,
                roleId: null,
                selectedUserId: null,
            },
            query: EmailTemplateQuery,
            refreshData: false,
        });

        const fetchedTemplate = fetchedTemplateData?.templates?.emailTemplate ?? null;
        setDefaultTemplateData(fetchedTemplate);
        return fetchedTemplate;
    };

    const handleItemSelect = async (item: SidebarItem): Promise<void> => {
        setSelectedSidebarItemId(item.id);
        const selectedTemplate = list.find(t => t.emailTemplateGuid === item.id);
        await fetchSelectedTemplate(selectedTemplate);
    };

    useEffect(() => {
        if (!firstTemplateId) {
            setSelectedSidebarItemId(null);
            return;
        }

        if (selectedSidebarItemId && list.some((item) => item.emailTemplateGuid === selectedSidebarItemId)) {
            return;
        }

        setSelectedSidebarItemId(firstTemplateId);
        void fetchSelectedTemplate(firstTemplate);
    }, [firstTemplateId, selectedSidebarItemId, list]);

    useEffect(() => {
        void fetchDefaultTemplate();
    }, []);

    // ── Handlers ───────────────────────────────────────────────────────────────

    const openCreate = () => {
        setIsCreateMode(true);

        if (!defaultTemplateData) {
            void fetchDefaultTemplate();
        }

        setDialogOpen(true);
    };

    const openEdit = (t: EmailTemplateList) => {
        setIsCreateMode(false);

        if (selectedTemplateData?.emailTemplateGuid !== t.emailTemplateGuid) {
            setSelectedTemplateData(t as unknown as EmailTemplate);
        }
        setDialogOpen(true);
    };

    const handleSave = async (template: EmailTemplate | null): Promise<void> => {

        if (template) {
            const templateToRefresh = list.find(item => item.emailTemplateGuid === template.emailTemplateGuid) ?? null;
            await fetchSelectedTemplate(templateToRefresh ?? undefined, true);
        }

        setIsCreateMode(false);
        setDialogOpen(false);
    };

    const onConfirmDelete = () => {
        if (!deleteTarget) return;
        // TODO: wire up to mutation

        client.mutate<DeleteEmailTemplateData, DeleteEmailTemplateParams>({
            mutation: DeleteEmailTemplate,
            variables: {
                emailType: EmailTemplateTypeEnum[props.emailType],
                isInternalRole: deleteTarget.isInternalRole,
                roleId: deleteTarget.roleGuid,
                selectedUserId: deleteTarget.userGuid,
                emailStyle: null,
                integrationTemplateId: deleteTarget.integrationTemplateId,
                emailTemplateGuid: deleteTarget.emailTemplateGuid,
            }
        }).then((results) => {
            if (results.data?.deleteEmailTemplate) {
                showNotification(null, 'Successfully deleted', 'info');
            } else {
                showNotification('Failed to delete email template', 'The server did not confirm the deletion.', 'error');
            }
        }).catch((error) => {
            showNotification('Failed to delete email template', error.message, 'error');
        }).finally(() => {
            setDeleteTarget(undefined);
        });

    };

    // ── renderContent ──────────────────────────────────────────────────────────

    const renderContent = (selectedItem: SidebarItem | null): React.ReactNode => {


        const summaryTemplate = list.find(t => t.emailTemplateGuid === selectedItem?.id);
        const template = selectedTemplateData?.emailTemplateGuid === selectedItem?.id
            ? selectedTemplateData
            : null;

        if ((selectedTemplateLoading && !template) || !selectedItem) {
            return (
                <div
                    style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16,
                    }}
                >
                    <div className={classes.emailCard} style={{ flex: 1 }}>
                        <div className={classes.emailBody}>
                            <SkeletonLoader 
                                rows={10}
                                columns={1}
                                variant="text"
                            />
                        </div>
                    </div>
                </div>
            );
        }
        
        if (!summaryTemplate) return null;

        const templateBody = template?.template ?? summaryTemplate.template;
        const templateFiles = template?.files ?? [];

        return (
            <>
                <div className={classes.emailCard}> 
                    <div
                        className={classes.emailBody}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: styleMergeFields(templateBody) }}
                    />

                    {template?.messageType !== MessageTypeEnum.SMS && templateFiles.length > 0 && (
                        <div className={classes.attachmentsWrap}>
                            <div className={classes.attachmentsTitle}>Attachments</div>
                            <div className={classes.attachmentsList}>
                                {templateFiles.map(file => {
                                    const extension = file.name?.includes('.')
                                        ? file.name.split('.').pop()?.toUpperCase()
                                        : 'FILE';

                                    return (
                                        <a
                                            key={file.guidID}
                                            className={`${classes.attachmentLink} ${!file.url ? classes.attachmentLinkDisabled : ''}`}
                                            href={file.url ?? undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download
                                            aria-disabled={!file.url}
                                            onClick={(event) => {
                                                if (!file.url) {
                                                    event.preventDefault();
                                                }
                                            }}
                                        >
                                            <div className={classes.attachmentLeft}>
                                                <span className={classes.attachmentIconWrap}>
                                                    <AttachFileIcon />
                                                </span>

                                                <div className={classes.attachmentMeta}>
                                                    <span className={classes.attachmentName}>{file.name}</span>
                                                    <span className={classes.attachmentSub}>
                                                        {file.url ? 'Click to download' : 'File is unavailable'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className={classes.attachmentRight}>
                                                <span className={classes.attachmentExt}>{extension}</span>
                                                {file.url && (
                                                    <span className={classes.attachmentDownload}>
                                                        <CloudDownloadIcon />
                                                    </span>
                                                )}
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <>
            <FilteredVerticalTabPage
                key={firstTemplateId ?? 'matter-email-template-empty'}
                enableSearch
                searchPlaceholder="Search templates…"
                sidebarGroups={TEMPLATE_GROUPS}
                sidebarItems={sidebarItems}
                defaultSelectedId={firstTemplateId ?? undefined}
                selectedId={selectedSidebarItemId}
                sidebarLoading={emailTemplateListQuery.loading}
                onItemSelect={handleItemSelect}
                renderContent={renderContent}
                sidebar={{
                    // onEdit: async (item) => openEdit(list.find(t => t.emailTemplateGuid === item.id)!),
                    // onDelete: (item) => setDeleteTarget(list.find(t => t.emailTemplateGuid === item.id)),
                    onCreate: openCreate,
                }}
                contentControl={{
                    title: selectedTemplateData?.name ?? '',
                    subtitle: selectedTemplateData?.description ?? '',
                    actions: {
                        onEdit: async (item) => openEdit(list.find(t => t.emailTemplateGuid === item.id)!),
                        onDelete: (item) => setDeleteTarget(list.find(t => t.emailTemplateGuid === item.id)),
                        // onCreate: openCreate,
                    },
                    loading: selectedTemplateLoading || selectedTemplateData === null,
                }}
                
            />

            {dialogOpen && (
                <EmailTemplateDialog
                    emailType={props.emailType}
                    messageType={props.messageType}
                    areaOfLawMultiple={props.areaOfLawMultiple}
                    title="Email Template"
                    subtitle="Create or edit an email template"
                    open={dialogOpen}
                    selectedTemplate={isCreateMode ? defaultTemplateData : selectedTemplateData}
                    onClose={() => {
                        setIsCreateMode(false);
                        setDialogOpen(false);
                    }}
                    onSave={handleSave}
                    showFields={props.showFields}
                    isCreate={isCreateMode}

                />
            )}

            {deleteTarget && (
                <DeleteConfirmationDialog
                    open={Boolean(deleteTarget)}
                    onClose={() => setDeleteTarget(undefined)}
                    title="Delete Template"
                    description="Are you sure you want to delete this template?"
                    confirmLabel="Yes, delete template"
                    onConfirm={onConfirmDelete}
                    itemName={deleteTarget?.subject}
                />
            )}
        </>
    );
};

export interface EmailTemplateQueryParams {
    isInternalRole: boolean | null;
    roleId: string | null;
    selectedUserId: string | null;
    emailType: string | null;
    emailStyle: string | null;
    integrationTemplateId: string | null;
    areaOfLaw: string | null;
    emailTemplateGuid: string | null;
    messageType?: string | null;
}

export interface EmailTemplateData {
    loading?: boolean;
    networkStatus?: number;
    templates: Templates;
}

export interface Templates {
    emailTemplate: EmailTemplate;
}

export interface EmailTemplate {
    emailTemplateGuid: string;
    emailType:         number;
    isInternalRole:    null;
    roleGuid:          null;
    subject:           string;
    template:          string;
    emailStyle:        number;
    userGuid:          null;
    ccAssistant:       boolean;
    matchedPropertys:  MatchedPropertys;
    integrationTemplateId: string;
    integrationTemplateName: string;
    description: string;
    messageType:             number;
    messageTypeName:         string;
    name:                    string;
    isActive:                boolean;
    files: DocumentFile[];
}
export interface MatchedPropertys {
    entity:          string;
    matchedProperty: MatchedProperty[];
}
export interface MatchedProperty {
    key:                  string;
    keyDescription:       string;
    recipient:            string;
    recipientDescription: string;
    scope:                string;
    scopeDescription:     string;
    matterCategory:      string;
    matterCategoryDescription: string;
    matterType:         string;
    matterTypeDescription: string;
}

export const EmailTemplateQuery = gql`
    query EmailTemplate(
        $isInternalRole: Boolean,
        $roleId: String,
        $selectedUserId: String,
        $emailType: EmailType,
        $emailStyle: EmailStyle,
        $integrationTemplateId: String,
        $areaOfLaw: String,
        $emailTemplateGuid: String,
        $messageType: MessageType
    ) {
        templates {
        emailTemplate(
            isInternalRole: $isInternalRole,
            roleId: $roleId,
            selectedUserId: $selectedUserId,
            emailType: $emailType,
            emailStyle: $emailStyle,
            integrationTemplateId: $integrationTemplateId,
            areaOfLaw: $areaOfLaw,
            emailTemplateGuid: $emailTemplateGuid,
            messageType: $messageType
        )   {
                emailTemplateGuid
                emailType
                isInternalRole
                roleGuid
                subject
                template
                emailStyle
                userGuid
                ccAssistant
                integrationTemplateId,
                integrationTemplateName
                description
                messageType
                messageTypeName
                name
                isActive
                matchedPropertys {
                    entity
                    matchedProperty {
                        key
                        keyDescription
                        recipient
                        recipientDescription
                        scope
                        scopeDescription
                        matterCategory
                        matterCategoryDescription
                        matterType
                        matterTypeDescription
                    }
                }
                files {
                    guidID
                    name
                    url
                }
            }
        }
    }
`;


interface DeleteEmailTemplateParams {
    emailType: string | null;
    isInternalRole: boolean | null;
    roleId: string | null;
    selectedUserId: string | null;
    emailStyle: string | null;
    integrationTemplateId: string | null;
    emailTemplateGuid: string | null;
}

interface DeleteEmailTemplateData {
    deleteEmailTemplate: boolean;
}

const DeleteEmailTemplate = gql`
    mutation DeleteEmailTemplate(
        $emailType: EmailType, 
        $isInternalRole: Boolean, 
        $roleId: String,
        $selectedUserId: String,
        $emailStyle: EmailStyle,
        $integrationTemplateId: String,
        $emailTemplateGuid: String
    ){
        deleteEmailTemplate(
            emailType: $emailType, 
            isInternalRole: $isInternalRole,  
            roleId: $roleId, 
            selectedUserId: $selectedUserId,
            emailStyle: $emailStyle,
            integrationTemplateId: $integrationTemplateId,
            emailTemplateGuid: $emailTemplateGuid
        )
    }
`;